import * as React from 'react';
import 'fontsource-roboto';
import '../app.css';
import {
  Box,
  createStyles,
  IconButton,
  makeStyles,
  Paper,
  Typography,
} from '@material-ui/core';
import type { GridColumns } from '@material-ui/data-grid';
import {
  DataGrid,
} from '@material-ui/data-grid';
import Message from '@material-ui/icons/Message';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import TableCellChip from '../TableCellChip';
import RepairJobActionMenu from './RepairJobActionMenu';
import type { IRepairItemJob } from './EstimatorInterfaces';
import globalUseStyles from '../../globalClasses';

interface IAllRepairJobsProps {
  repairItemJobs: IRepairItemJob[],
  handleDeleteRepairItemJob: (id: string) => void;
  handleOpenEditRepairItemJob: (id: string) => void;
}

const useStyles = makeStyles(() => createStyles({
  tableContainer: {
    boxShadow: 'none',
    border: 'none',
  },
  title: {
    fontWeight: 700,
    fontSize: 18,
  },
}));

const AllRepairJobs: React.FC<IAllRepairJobsProps> = (
  { repairItemJobs, handleDeleteRepairItemJob, handleOpenEditRepairItemJob },
) => {
  const classes = useStyles();
  const globalStyles = globalUseStyles();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedRepairJobId, setSelectedRepairJobId] = React.useState<string | null>(null);

  // action menu functions
  const handleVertIconClick = (event: React.MouseEvent<HTMLButtonElement>, jobId: string) => {
    if (anchorEl) {
      console.log();
    } else {
      setSelectedRepairJobId(jobId);
      setAnchorEl(event.currentTarget);
    }
  };
  const handleCloseMenu = () => {
    setSelectedRepairJobId(null);
    setAnchorEl(null);
  };

  const handleDeleteRepairJob = (jobId: string) => {
    handleCloseMenu();
    handleDeleteRepairItemJob(jobId);
  };

  const handleOpenEditRepairJob = (jobId: string) => {
    handleCloseMenu();
    handleOpenEditRepairItemJob(jobId);
  };

  // table functions
  const displayRepairJob = (repairJob: string) => {
    const hasNote = false;

    return (
      <Box>
        {repairJob}
        {' '}
        {hasNote && <Message color="error" />}
      </Box>
    );
  };

  const displayRepairItemsAffected = (itemLabels: string) => (
    <>
      {itemLabels.split(',').map((itemLabel) => (
        itemLabel === 'All Items'
          ? <TableCellChip label={itemLabel} backgroundColor="#DAEADC" fontColor="#6CAE75" />
          : (
            <Box pr={0.3}>
              <TableCellChip label={itemLabel} backgroundColor="#FDECCC" fontColor="#FAB635" />
            </Box>
          )
      ))}
    </>
  );

  const displayVertIcon = (repairId: string) => (
    <IconButton
      id={`estimator-kabob-${repairId}`}
      aria-label="more"
      aria-controls="long-menu"
      aria-haspopup="true"
      onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleVertIconClick(e, repairId)}
    >
      <MoreVertIcon />
    </IconButton>
  );

  const tableColumns: GridColumns = [
    {
      field: 'repairJob',
      headerName: 'Repair Job',
      flex: 0.33,
      renderCell: ({ value }) => (
        ((typeof (value) === 'string')
          ? displayRepairJob(value)
          : <></>
        )
      ),
    },
    {
      field: 'repairItemsAffected',
      headerName: 'Repair Item(s) Affected',
      flex: 1,
      renderCell: ({ value }) => (
        ((typeof (value) === 'string')
          ? displayRepairItemsAffected(value)
          : <></>
        )
      ),
    },
    {
      field: 'id',
      type: 'string',
      headerName: 'Actions',
      flex: 0.20,
      renderCell: ({ value }) => (
        ((typeof (value) === 'string')
          ? displayVertIcon(value)
          : <></>
        )
      ),
    },
  ];

  const tableRows = repairItemJobs.map((rij) => ({
    id: rij.id,
    repairJob: rij.jobName,
    repairItemsAffected: rij?.repairItemLabels?.join()?.toString(),
  }));

  return (
    <Box component="span">
      <Paper>
        <Box p={2}>
          <Box display="flex">
            <Box flexGrow={1}>
              <Typography variant="h6" color="primary" className={classes.title}>{'All Repair Jobs'}</Typography>
            </Box>
          </Box>
        </Box>
        <DataGrid
          rows={tableRows || []}
          columns={tableColumns}
          className={globalStyles.transparentDataGrid}
          autoHeight
          hideFooter
          disableColumnMenu
        />

        {selectedRepairJobId && anchorEl && (
          <RepairJobActionMenu
            repairJobId={selectedRepairJobId}
            anchorEl={anchorEl}
            handleDeleteRepairJob={handleDeleteRepairJob}
            handleOpenEditRepairJob={handleOpenEditRepairJob}
            handleClose={handleCloseMenu}
          />
        )}
      </Paper>
    </Box>
  );
};

export default AllRepairJobs;
